import moment from 'moment-timezone';
import { appTimezone } from '../../support/dateUtils';
import type { Recurrence } from '../../types/journeysScheduleTypes';

type RecurrenceState = {
  daily: boolean;
  days: Array<boolean>;
};

const range = (n) => {
  return Array.from({ length: n }, (value, key) => key + 1);
};

const DAYS = moment.weekdays();

/**
 * Get minutes in 5 minute interval
 * The minutes will be appended with a '0' if its a single digit
 */
export const FIVE_MINUTE_INTERVALS = [0, ...range(11)].map((t) => {
  const minute = t * 5;
  return {
    value: minute,
    label: ('0' + minute.toString()).slice(-2),
  };
});

export const HOURS = range(12).map((t) => ({
  value: t,
  label: ('0' + t.toString()).slice(-2),
}));

export const MERIDIEMS = [
  {
    value: 'am',
    label: 'AM',
  },
  {
    value: 'pm',
    label: 'PM',
  },
];

export const TIME = {
  minutes: FIVE_MINUTE_INTERVALS,
  hours: HOURS,
  meridiems: MERIDIEMS,
  selected_minute: FIVE_MINUTE_INTERVALS[0],
  selected_hour: HOURS[0],
  selected_meridiem: MERIDIEMS[0],
};

const ERROR_CODES = {
  missingStartAndStop: 1,
  invalidDateTime: 2,
  invalidDateTimeForRecurring: 3,
  invalidRecurring: 4,
  invalidDaySelected: 5,
  missingTimezone: 6,
};

export const ERRORS = [
  {
    /* 0 = no error */
  },
  {
    /* 1 = disable save, no error. Start and Stop times are unchecked */
  },
  {
    title: 'Invalid Schedule',
    text: 'Please make sure that your start time is before your stop time and that they are both in the future.',
  },
  {
    title: 'Invalid Schedule',
    text: 'You must have a start and stop time to schedule Journeys on a recurring basis.',
  },
  {
    title: 'Invalid Schedule',
    text: 'You must select at least one weekday to repeat your Journey weekly.',
  },
  {
    title: 'Invalid Schedule',
    text: 'Please update your start date to match your selected weekdays.',
  },
  {
    title: 'Invalid Schedule',
    text: 'Please select a timezone.',
  },
];

export class ScheduleJourneyService {
  public static getEndTime = (schedule, timezone) => {
    const { recurrence } = schedule;
    if (recurrence) {
      const stopTime = moment.tz(recurrence.next_stop, timezone);
      return stopTime.isAfter(moment.tz(recurrence.next_start, timezone))
        ? stopTime
        : this.getNextValidTime(stopTime, recurrence.stop);
    } else {
      return schedule.end_date_time
        ? moment.tz(schedule.end_date_time, timezone)
        : moment.tz(schedule.start_date_time, timezone).add(1, 'hours');
    }
  };

  public static getInitialTimeZone = (dateTime) => {
    return dateTime ? dateTime.match(/[a-zA-Z_\-/]+$/g)[0] : appTimezone;
  };

  public static getStartTime = (schedule, timezone) => {
    const { recurrence } = schedule;
    if (recurrence) {
      return moment.tz(recurrence.next_start, timezone);
    } else {
      return moment.tz(schedule.start_date_time, timezone);
    }
  };

  public static getNextValidDayOfWeek = (time, dayPattern) => {
    const dayOfWeek = time.day();
    const scheduledDays = dayPattern.split(',').map((v) => parseInt(v));
    const daysToAdd = (scheduledDays.find((day) => day > dayOfWeek) || 7 + scheduledDays[0]) - dayOfWeek;
    return time.add(daysToAdd, 'days');
  };

  /**
   * Handles day of week position of cron only
   * @param  {object} time moment object
   * @param  {string} cron cron expression
   * @return {object}      moment object
   */
  public static getNextValidTime = (time, cron) => {
    const dayPattern = cron.split(' ')[5];
    return dayPattern === '*' ? time.add(1, 'days') : this.getNextValidDayOfWeek(time, dayPattern);
  };

  public static getInitialTime = (dateTime) => {
    const hour = dateTime.hour() % 12;
    const selected_hour = HOURS[hour == 0 ? 11 : hour - 1];
    const minute = `${dateTime.minute() / 5}`;
    const selected_minute = FIVE_MINUTE_INTERVALS[parseInt(minute)];
    return {
      ...TIME,
      selected_minute,
      selected_hour,
      selected_meridiem: MERIDIEMS[Math.floor(dateTime.hour() / 12)],
    };
  };

  /**
   * Subtract 1 from day-of-week cron. java package org.quartz.CronExpression
   * uses 1-7 (SUN-SAT). On the other hand, moment uses 0-6 (SUN-SAT)
   * Refer http://www.quartz-scheduler.org/api/2.2.1/org/quartz/CronExpression.html
   * for more details.
   */
  public static getRecurrenceFromCron(recurrence: Recurrence) {
    if (recurrence) {
      const dayPattern = recurrence.start.split(' ')[5];
      const daily = dayPattern === '*';
      const recurringDays = Array.from(
        {
          length: 7,
        },
        () => false
      );

      if (!daily) {
        dayPattern.split(',').forEach((value) => (recurringDays[parseInt(value) - 1] = true));
      }

      return {
        daily,
        days: recurringDays,
      };
    }

    return null;
  }

  /**
   * get cron expression from given start, end and recurrence
   * Add 1 to day-of-week cron. java package org.quartz.CronExpression
   * uses 1-7 (SUN-SAT). On the other hand, moment uses 0-6 (SUN-SAT)
   * Refer http://www.quartz-scheduler.org/api/2.2.1/org/quartz/CronExpression.html
   * for more details.
   * @param  {object} recurrence { daily: boolean, days: Array<boolean> }
   * @param  {string} start      start date time
   * @param  {string} stop       stop date time
   * @param  {boolean} addDays    add offset to cron
   * @return {string}            cron
   */
  public static getCron(recurrence: RecurrenceState, start: string, stop: string, addDays: boolean) {
    const time = moment(addDays ? stop : start, 'YYYY-MM-DD HH:mm:ss zzzz');
    let dayOffset = 0;

    if (addDays) {
      const endTime = moment(time).endOf('day');
      const startTime = moment(start, 'YYYY-MM-DD HH:mm:ss zzzz').startOf('day');
      dayOffset = endTime.diff(startTime, 'days');
    }

    const daysCron = recurrence.daily
      ? '*'
      : recurrence.days
          .map((value, i) => (value ? ((i + dayOffset) % 7) + 1 : value))
          .filter((value) => Number.isInteger(value))
          .sort()
          .join(',');
    return `0 ${time.minute()} ${time.hour()} ? * ${daysCron}`;
  }

  /**
   * Returns comma separated days of week based on the given cron
   * Subtract 1 from day-of-week cron. java package org.quartz.CronExpression
   * uses 1-7 (SUN-SAT). On the other hand, moment uses 0-6 (SUN-SAT)
   * Refer http://www.quartz-scheduler.org/api/2.2.1/org/quartz/CronExpression.html
   * for more details.
   * @param  {object} recurrence Recurrence
   * @return {string}            Days of week
   */
  public static getRepeatTextFromCron(recurrence: Recurrence) {
    const dayPattern = recurrence.start.split(' ')[5];
    const repeatDays = dayPattern.split(',');
    return dayPattern === '*'
      ? 'Daily'
      : `Weekly on ${repeatDays
          .map((day) => {
            return DAYS[parseInt(day) - 1];
          })
          .join(', ')}`;
  }

  public static convertDisplayToRecurrence(recurrence, start, end) {
    if (recurrence) {
      const startCron = this.getCron(recurrence, start, end, false);
      const stopCron = this.getCron(recurrence, start, end, true);
      return {
        next_start: start,
        next_stop: end,
        start: startCron,
        stop: stopCron,
      };
    }
  }

  public static validateSchedule(start, end, timezone, recurrence) {
    if (!timezone) {
      return ERROR_CODES.missingTimezone;
    }
    if (recurrence) {
      return this.validateRecurringSchedule(start, end, timezone, recurrence);
    } else {
      return this.validateDateTime(start, end, timezone);
    }
  }

  /**
   * Validatees date time for recurring schedule
   *  - Return invalidRecurring if start and end are unchecked
   *  - Return invalidDateTimeForRecurring if no day is selected for weekly
   *  - Return validateDateTime for date time validation if above two pass
   * @param  {object} start    start date time checked object
   * @param  {object} end      end date time checked object
   * @param  {string} timezone Formatter: "VV"
   * @param  {object} recurrence { daily: boolean, days: Array<boolean> }
   * @return {number}            ERROR_CODES
   */
  public static validateRecurringSchedule(start, end, timezone, recurrence) {
    const dayOfWeek = this.getMomentFromDateTimeTimeZone(start, timezone).day();
    if (start.checked && end.checked) {
      if (!recurrence.daily && !recurrence.days.find((day) => day)) {
        return ERROR_CODES.invalidRecurring;
      }
      if (!recurrence.daily && !recurrence.days[dayOfWeek]) {
        return ERROR_CODES.invalidDaySelected;
      }
      return this.validateDateTime(start, end, timezone);
    } else {
      return ERROR_CODES.invalidDateTimeForRecurring;
    }
  }

  /**
   * Validates given date and time
   *  - Return error without message if start and end are unchecked
   *  - Return invalidDateTime message if start is after end
   *  - Return invalidDateTime if end is in past
   * @param  {object} start    start date time checked object
   * @param  {object} end      end date time checked object
   * @param  {string} timezone Formatter: "VV"
   * @return {number}          ERROR_CODES
   */
  public static validateDateTime(start, end, timezone) {
    const startDateTime = this.getMomentFromDateTimeTimeZone(start, timezone);
    const endDateTime = this.getMomentFromDateTimeTimeZone(end, timezone);

    if (!start.checked && !end.checked) {
      return ERROR_CODES.missingStartAndStop;
    } else if (!start.checked) {
      return endDateTime.isAfter(moment()) ? 0 : ERROR_CODES.invalidDateTime;
    } else if (start.checked && end.checked) {
      return startDateTime.isBefore(endDateTime) ? 0 : ERROR_CODES.invalidDateTime;
    }
    return 0;
  }

  /**
   * @param timezone {string} Time zone value
   */
  public static getMomentFromDateTimeTimeZone(dateTime, timezone) {
    return moment.tz(this.formatDateTime(dateTime), timezone);
  }

  /**
   * Formats datetime string from given dateTime object and timezone
   * @param dateTime {Object} Date time object
   * @param timezone {string} Time zone value
   * @return {string} returns date time and time zone in the format:
   *                          'YYYY-MM-DD HH:mm:ss zzzz'
   */
  public static formatDateTimeTimeZone(dateTime, timezone) {
    if (!dateTime.checked) {
      return null;
    }
    return `${this.formatDateTime(dateTime)} ${timezone}`;
  }

  public static formatDateTime(dateTime) {
    if (!dateTime.checked) {
      return null;
    }
    return `${dateTime.selectedDate} ${this.getMilitaryTime(dateTime.time)}`;
  }

  /**
   * Converts 12 hour time to 24
   *
   * @param time {Object} - Contains hour, minute, am/pm string fields
   * @return {string} - With format hh:mm:ss
   */
  public static getMilitaryTime(time) {
    const hour =
      time.selected_meridiem.value === 'pm' ? ((time.selected_hour.value % 12) + 12) % 24 : time.selected_hour.value % 12;
    const hourString = `${hour < 10 ? '0' : ''}${hour.toString()}`;
    return `${hourString}:${time.selected_minute.label}:00`;
  }

  public static getNextOccurrence = (cronExpression, timezone, referenceTime) => {
    // Parse the cron expression (we assume it's in the format "second minute hour ? * dayOfWeek")
    const [second, minute, hour, , , daysOfWeek] = cronExpression.split(' ');

    // Set the next occurrence date starting from the reference time
    const nextDate = referenceTime.clone().tz(timezone).startOf('second');

    // Adjust the time for the next occurrence
    nextDate.set({
      second: parseInt(second),
      minute: parseInt(minute),
      hour: parseInt(hour),
    });

    if (daysOfWeek === '*') {
      return nextDate.format('YYYY-MM-DD HH:mm:ss Z');
    }

    // Handle multiple days in the cron expression
    const cronDays = daysOfWeek.split(',').map((day) => parseInt(day) - 1); // Convert 1-7 (Java) to 0-6 (Moment)
    const currentDay = nextDate.day();

    // Find the next valid day in the cronDays array
    const nextDay = cronDays.find((day) => day > currentDay || (day === currentDay && nextDate.isAfter(referenceTime)));

    if (nextDay !== undefined) {
      // If a valid day is found this week, set the next occurrence to that day
      nextDate.day(nextDay);
    } else {
      // Otherwise, find the earliest day in the array and set it for the next week
      const earliestDay = Math.min(...cronDays);
      nextDate.add(1, 'week').day(earliestDay);
    }

    // Return the formatted date with timezone
    return nextDate.format('YYYY-MM-DD HH:mm:ss Z');
  };

  public static getNextStartStop = (schedule) => {
    const { start_date_time, recurrence } = schedule;

    // Extract the timezone from the start_date_time and set the reference time
    const startDateTimeParts = start_date_time.split(' ');
    const timezone = startDateTimeParts.pop(); // Extract the timezone part
    const referenceTime = moment(start_date_time, 'YYYY-MM-DD HH:mm:ss Z').tz(timezone); // Parse start_date_time as reference

    // Calculate next_start and next_stop based on recurrence
    const next_start = this.getNextOccurrence(recurrence.start, timezone, referenceTime);
    const next_stop = this.getNextOccurrence(recurrence.stop, timezone, referenceTime);

    return {
      next_start,
      next_stop,
    };
  };
}
